import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { acessoEfetivo, moduloDaPagina, type AjusteAcesso } from '@malgoos/shared';
import { Botao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { ArvoreAcesso, usePaginasPorModulo } from './ArvoreAcesso';
import { ajustesDaDiferenca, retirarPaginasQueOAtorNaoAbre, type EstadoAcesso } from './arvore-acesso';
import { usePapeis, usePermissoes } from './MatrizPermissoes';
import type { Membro, Papel } from './tipos';

/**
 * Val1 (papel por sub-sistema, ficha 2026-10-28): a exceção de acesso de uma pessoa, no drawer do membro. A árvore parte da
 * soma dos papéis dela; o que muda vira exceção (conceder ou retirar ação ou página), com o selo "exceção" em cada item e o
 * selo "acesso personalizado" na lista. A regra é atender por papéis: a exceção é pontual e fica na auditoria.
 */
export function AcessoDoMembro({ membro, podeEditar, aoSalvar }: { membro: Membro; podeEditar: boolean; aoSalvar: () => void }) {
  const { t } = useTranslation('admin');
  const papeis = usePapeis();
  const perms = usePermissoes();
  const atual = useQuery({ queryKey: ['/admin/membros', membro.id, 'acesso'], queryFn: () => get<{ ajustes: AjusteAcesso[] }>(`/admin/membros/${membro.id}/acesso`) });
  return (
    <section data-acesso-membro="">
      <h3 className="kicker mb-1 flex flex-wrap items-center gap-2">
        {t('usuarios.acessoPessoa.titulo')}
        {membro.acesso_personalizado && <Selo cor="roxo">{t('usuarios.acessoPersonalizado')}</Selo>}
      </h3>
      {/* Review rval1-12 visual (B2): o desativado só vê; o texto diz por quê e como liberar, em vez de mandar marcar. */}
      {membro.ativo ? (
        <p className="mb-2 text-xs text-tinta-3">{t('usuarios.acessoPessoa.ajuda')}</p>
      ) : (
        <p data-acesso-desativado="" className="mb-2 text-xs text-tinta-3">
          {t('usuarios.acessoPessoa.desativado')}
        </p>
      )}
      <Estado consulta={atual}>
        {(a) => (
          <Estado consulta={papeis}>
            {(p) => (
              <Estado consulta={perms}>
                {(c) => <Editor key={JSON.stringify(a.ajustes)} membro={membro} papeis={p.itens} catalogo={c.itens} ajustes={a.ajustes} podeEditar={podeEditar} aoSalvar={aoSalvar} />}
              </Estado>
            )}
          </Estado>
        )}
      </Estado>
    </section>
  );
}

function Editor({
  membro,
  papeis,
  catalogo,
  ajustes,
  podeEditar,
  aoSalvar,
}: {
  membro: Membro;
  papeis: Papel[];
  catalogo: Parameters<typeof ArvoreAcesso>[0]['permissoes'];
  ajustes: AjusteAcesso[];
  podeEditar: boolean;
  aoSalvar: () => void;
}) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const paginas = usePaginasPorModulo();
  const todasPaginas = useMemo(() => [...paginas.entries()].flatMap(([slug, abas]) => abas.map((a) => `${slug}.${a}`)), [paginas]);
  const base = useMemo<EstadoAcesso>(() => {
    const doMembro = papeis.filter((p) => membro.papeis.some((m) => m.id === p.id)).map((p) => ({ permissoes: new Set(p.efetivas), negadas: p.paginas_negadas ?? [] }));
    const a = acessoEfetivo(doMembro);
    return { permissoes: a.permissoes, negadas: a.paginasNegadas };
  }, [papeis, membro.papeis]);
  const inicial = useMemo<EstadoAcesso>(() => {
    const doMembro = papeis.filter((p) => membro.papeis.some((m) => m.id === p.id)).map((p) => ({ permissoes: new Set(p.efetivas), negadas: p.paginas_negadas ?? [] }));
    const a = acessoEfetivo(doMembro, ajustes);
    return { permissoes: a.permissoes, negadas: a.paginasNegadas };
  }, [papeis, membro.papeis, ajustes]);
  const [estado, setEstadoLivre] = useState<EstadoAcesso>(inicial);
  // Review rval1-8 (SEG-M1): a ação concedida leva junto a retirada das páginas do módulo que quem edita não abre (o servidor
  // grava o mesmo). A árvore já mostra essas páginas desmarcadas, com o selo "exceção", e a lista delas abaixo.
  const sessao = useSessao();
  const atorAbre = (pg: string) => sessao.paginaLiberada(moduloDaPagina(pg), pg.slice(pg.indexOf('.') + 1));
  const setEstado = (e: EstadoAcesso) => setEstadoLivre(retirarPaginasQueOAtorNaoAbre(inicial, e, paginas, atorAbre).estado);
  const retiradasJunto = retirarPaginasQueOAtorNaoAbre(inicial, estado, paginas, atorAbre).retiradas;
  // As retiradas gravadas na exceção (também as que o papel desmarca): aparecem como exceção na árvore.
  const retiradas = new Set([...ajustes.filter((a) => a.tipo === 'pagina' && !a.concede).map((a) => a.codigo), ...retiradasJunto]);
  const catalogoCodigos = useMemo(() => new Set(catalogo.map((p) => p.codigo)), [catalogo]);
  const salvar = useMutation({
    mutationFn: (lista: AjusteAcesso[]) => api('PUT', `/admin/membros/${membro.id}/acesso`, { ajustes: lista }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
      toast.sucesso(t('usuarios.acessoPessoa.salvo'));
      aoSalvar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Só o que está no catálogo dos módulos instalados vira exceção (a soma dos papéis de fábrica traz todas as do catálogo).
  const diferenca = () => ajustesDaDiferenca(base, estado, todasPaginas, ajustes).filter((a) => a.tipo === 'pagina' || catalogoCodigos.has(a.codigo));
  return (
    <div className="flex flex-col gap-3">
      <ArvoreAcesso permissoes={catalogo} estado={estado} base={base} retiradas={retiradas} somenteLeitura={!podeEditar} aoMudar={setEstado} sujeito="pessoa" />
      {retiradasJunto.length > 0 && (
        <p className="text-xs text-tinta-3" data-retiradas-junto="">
          {t('usuarios.acessoPessoa.retiradasJunto', { paginas: retiradasJunto.map((pg) => tn(`${moduloDaPagina(pg)}:abas.${pg.slice(pg.indexOf('.') + 1)}`)).join(', ') })}
        </p>
      )}
      {podeEditar && (
        <div className="flex flex-wrap gap-2">
          {/* Review rval1-1 (VIS-B1): o primário do drawer é o salvar dos papéis; a exceção é secundária. */}
          <Botao variante="secundario" carregando={salvar.isPending} onClick={() => salvar.mutate(diferenca())}>
            {t('usuarios.acessoPessoa.salvar')}
          </Botao>
          {ajustes.length > 0 && (
            <Botao variante="fantasma" disabled={salvar.isPending} onClick={() => salvar.mutate([])}>
              {t('usuarios.acessoPessoa.voltar')}
            </Botao>
          )}
          <Botao variante="fantasma" disabled={salvar.isPending} onClick={() => setEstado(inicial)}>
            {tn('comum.cancelar')}
          </Botao>
        </div>
      )}
    </div>
  );
}
